import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {Application} from "../../model/application";
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { ApplicationService } from 'src/app/services/application/application.service';
import { ColumnDef } from 'src/app/model/column-definition';
import { MatPaginator } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { AppConstants } from 'src/app/constants/app-constants';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

/**
 * ManualReviewPageComponent represents the 'manual-review' menu
 * Displays applications with status 'access' or 'select'
 */
@Component({
  selector: 'app-manual-review-page',
  templateUrl: './manual-review-page.component.html',
  styleUrl: './manual-review-page.component.scss'
})
export class ManualReviewPageComponent implements AfterViewInit {
  /** The paginator to apply to the table */
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator | null = null;

  /** The sort element in our table */
  @ViewChild(MatSort, {static: true}) sort: MatSort = new MatSort();

  /** The table of applications */
  @ViewChild(MatTable) table!: MatTable<Application>;

  /** The data to be rendered in the table */
  public source = new MatTableDataSource<Application>([]);

  /** How long it has been since the last refresh */
  public timeSinceLastRefresh: string = "0:00";

  /** Subscription to update new suppliers */
  private _sub: Subscription | null = null;

  /** Column definitions for the table */
  public colDefs: ColumnDef[] = [
    {header: "Application ID", field: "applicationGuid"},
    {header: "Name", field: "name"},
    {header: "Company Name", field: "businessName"},
    //{header: "Applied", field: "appliedOn", format: "date", toStringTransform: (x) => new Date(x).toDateString()},
    {header: "Spread", field: "borrowerApr", format: "percentage"},
    {header: "Phone Number", field: "phoneNumber", format: "phone"},
    {header: "Amount", field: "approvedAmount", format: "currency", toStringTransform: (x) => `$${x}`},
    {header: "Status", field: "status", format: "status"},
  ]
  public displayedCols: string[] = [...this.colDefs.map(x => x.field), 'actions'];

  /**
   * Constructor for the manual review page to initialize services and set a base snapshot 
   * 
   * @param _applicationService 
   */
  constructor(
    private _applicationService: ApplicationService,
    private _router: Router,
    private _toastr: ToastrService,
  ) {
    // Subscribe to timer changes
    this._applicationService.timeSinceLastRefresh$.subscribe(
      (str: string) => {
        this.timeSinceLastRefresh = str;
      });
  }

  /**
   * Initialize the sort, paginator, and application data source
   */
  ngOnInit() {
    this.source.sort = this.sort;
    this.source.paginator = this.paginator;

    // Add a filter predicate to search with our custom toString method with delimieters
    this.source.filterPredicate = (data: Application, filter: string): boolean => {
      return data.toString(this.colDefs).includes(filter);
    }

    // Get the applications from the service
    this._sub = this._applicationService.applications$.subscribe(
      (resp: Application[]) => {
        this._populateSource(resp);
      });

    this._populateSource(this._applicationService.getApplications());
  }

  /**
   * After the table gets initialized, add a sorter and custom sorting functions
   */
  ngAfterViewInit() {
    this.source.sortingDataAccessor = (item: Application, property: string): string | number => {
      return (item as any)[property];
    }
  }

  /**
   * When the component gets destroyed (user navigates away, unsubscribe from changes)
   */
  public ngOnDestroy() {
    this._sub?.unsubscribe();
  }

  /**
   * Apply a filter to the table
   */
  public applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.source.filter = filterValue.trim().toLowerCase();
  }

  /**
   * Approve a manual application
   * 
   * @param application The application to approve
   */
  public onApprove(application: Application): void {
    this._applicationService.approveManualApplication(application).subscribe({
      next: (success: boolean) => {
        const msg = success ? 'Application approved successfully' : 'Failed to approve application';
        success ? this._toastr.success(msg) : this._toastr.error(msg);
      },
      error: (err) => {
        console.error('Error denying application:', err);
        this._toastr.error('Error denying application');
      }
    });
  }

  /**
   * Deny a manual application
   * 
   * @param application The application to deny
   */
  public onDeny(application: Application): void {
    this._applicationService.denyManualApplication(application).subscribe({
      next: (success: boolean) => {
        const msg = success ? 'Application denied successfully' : 'Failed to deny application';
        success ? this._toastr.success(msg) : this._toastr.error(msg);
      },
      error: (err) => {
        console.error('Error denying application:', err);
        this._toastr.error('Error denying application');
      }
    });
  }

  /**
   * Populate the data source meticulously so it doesn't lag the application
   * Note: I don't know if async is required, I'm just paranoid
   */
  private async _populateSource(incoming: Application[]): Promise<void> {
    // Filter for only 'access' or 'select' status applications
    incoming = incoming.filter(app => 
      app.status.toLowerCase() === 'access' || app.status.toLowerCase() === 'select'
    );

    // Don't update the table when the user is not on the page
    if (this._router.url.replace("/", "") !== AppConstants.MENU_MANUAL_REVIEW) return;

    let source = this.source.data;
    const current = this.source.data.map((x: Application) => x.copyOf());

    // For each incoming application, determine if it exists in the source. 
    const update = async () => {
      let sourceIndex = 0; // Using currentIndex isn't reliable because we need to decrement it

      for (let currentIndex = 0; currentIndex < current.length; currentIndex++) {
        for (let incomingIndex = 0; incomingIndex < incoming.length; incomingIndex++) {
          // If application IDs are equal check for updates and remove from the lists
          if (incoming[incomingIndex].id === current[currentIndex].id) {

            // If the applications are not equal, update the current source to the new one
            if (!incoming[incomingIndex].equals(current[currentIndex])) {
              source[sourceIndex] = incoming[incomingIndex].copyOf();
            }

            // We've updated the application, so remove it from both tracking arrays
            incoming.splice(incomingIndex, 1);
            current.splice(currentIndex, 1);

            currentIndex--;
            break;
          }
        }

        sourceIndex++;
      }
    }
    await update();

    // Add new applications that we don't currently have
    const add = async () => {
      for (let newApplication of incoming) {
        source.push(newApplication);
      }
    }
    await add();

    // Remove old applications that have been removed
    const remove = async () => {
      for (let oldApplication of current) {
        source = source.filter(x => x.id !== oldApplication.id);
      }
    }
    await remove();

    // Update the table
    this.source.data = source;
  }
}
